import { useEffect, useRef, useState, type ReactNode } from 'react';
import { S, t } from '../../i18n/strings';
import { useActions, useSteve } from '../../state/context';
import styles from './saas.module.css';
import { isPromoCode } from './signup';

interface Props {
  /** Pré-rempli quand le visiteur arrive par la Steveletter. */
  readonly promo?: string;
  readonly onClose: () => void;
}

/**
 * La modale d'abonnement — le formulaire qu'aucun SaaS n'assume et que
 * celui-ci assume complètement.
 *
 * Tous les champs sont impossibles à remplir, chacun à sa façon : le nom est
 * verrouillé (il n'y a qu'un Steve), les jambes vont de 2 à 2, la couleur n'a
 * qu'une option, l'adresse du bac ne correspond jamais à rien, et la case
 * « je ne suis pas un poisson » se décoche toute seule au motif que la
 * vérification a conclu l'inverse. Le bouton d'envoi ne peut rien envoyer, et
 * le dit.
 *
 * Un seul champ marche vraiment : **le code promo**. Taper STEVE change
 * d'interface — c'est le mot secret du site, ramené là où on l'attend le moins.
 * Il faut le brancher ici explicitement : l'écoute globale des mots secrets
 * ignore volontairement la frappe dans les champs de saisie, et c'est une règle
 * qu'on ne touche pas.
 */
export function SaasSignupModal({ promo = '', onClose }: Props): ReactNode {
  const { lang } = useSteve();
  const actions = useActions();

  const [code, setCode] = useState(promo);
  const [tank, setTank] = useState('');
  const [human, setHuman] = useState(false);
  const closeRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    closeRef.current?.focus();
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('keydown', onKey);
    };
  }, [onClose]);

  // La case se décoche toute seule : la vérification a conclu que vous êtes un
  // poisson. Le message n'apparaît qu'après avoir essayé.
  useEffect(() => {
    if (!human) return;
    const id = window.setTimeout(() => {
      setHuman(false);
    }, 900);
    return () => {
      window.clearTimeout(id);
    };
  }, [human]);

  const [humanTried, setHumanTried] = useState(false);

  return (
    <div
      className={styles.modalBackdrop}
      onPointerDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div
        className={styles.modal}
        role="dialog"
        aria-modal="true"
        aria-label={t(S.saas.modal.title, lang)}
      >
        <div className={styles.modalHead}>
          <div>
            <h2 className={styles.modalTitle}>{t(S.saas.modal.title, lang)}</h2>
            <p className={styles.modalSubtitle}>{t(S.saas.modal.subtitle, lang)}</p>
          </div>
          <button
            ref={closeRef}
            type="button"
            className={styles.modalClose}
            aria-label={t(S.saas.modal.close, lang)}
            onClick={onClose}
          >
            ×
          </button>
        </div>

        <div className={styles.modalBody}>
          <div className={styles.field}>
            <label className={styles.fieldLabel} htmlFor="saas-name">
              {t(S.saas.modal.field_name, lang)}
            </label>
            <input
              id="saas-name"
              type="text"
              className={styles.fieldInput}
              value="Steve"
              readOnly
            />
            <p className={styles.fieldNote}>{t(S.saas.modal.field_name_note, lang)}</p>
          </div>

          <div className={styles.field}>
            <label className={styles.fieldLabel} htmlFor="saas-legs">
              {t(S.saas.modal.field_legs, lang)}
            </label>
            <input
              id="saas-legs"
              type="number"
              className={styles.fieldInput}
              min={2}
              max={2}
              value={2}
              readOnly
            />
            <p className={styles.fieldNote}>{t(S.saas.modal.field_legs_note, lang)}</p>
          </div>

          <div className={styles.field}>
            <label className={styles.fieldLabel} htmlFor="saas-colour">
              {t(S.saas.modal.field_colour, lang)}
            </label>
            <select id="saas-colour" className={styles.fieldInput} defaultValue="orange">
              <option value="orange">{t(S.saas.modal.field_colour_option, lang)}</option>
            </select>
            <p className={styles.fieldNote}>{t(S.saas.modal.field_colour_note, lang)}</p>
          </div>

          <div className={styles.field}>
            <label className={styles.fieldLabel} htmlFor="saas-tank">
              {t(S.saas.modal.field_tank, lang)}
            </label>
            <input
              id="saas-tank"
              type="text"
              className={styles.fieldInput}
              value={tank}
              placeholder={t(S.saas.modal.field_tank_placeholder, lang)}
              autoComplete="off"
              onChange={(event) => {
                setTank(event.currentTarget.value);
              }}
            />
            {/* Une autocomplétion qui ne complète jamais rien. */}
            {tank.trim() !== '' && (
              <p className={styles.fieldNote} role="status">
                {t(S.saas.modal.field_tank_note, lang)}
              </p>
            )}
          </div>

          <div className={styles.fieldWide}>
            <label className={styles.checkboxRow}>
              <input
                type="checkbox"
                className={styles.checkbox}
                checked={human}
                onChange={(event) => {
                  setHuman(event.currentTarget.checked);
                  if (event.currentTarget.checked) setHumanTried(true);
                }}
              />
              <span>{t(S.saas.modal.field_human, lang)}</span>
            </label>
            {humanTried && !human && (
              <p className={styles.fieldNote} role="status">
                {t(S.saas.modal.field_human_note, lang)}
              </p>
            )}
          </div>

          <div className={styles.fieldWide}>
            <label className={styles.fieldLabel} htmlFor="saas-promo">
              {t(S.saas.modal.field_promo, lang)}
            </label>
            <input
              id="saas-promo"
              type="text"
              className={styles.fieldInput}
              value={code}
              placeholder={t(S.saas.modal.field_promo_placeholder, lang)}
              autoComplete="off"
              onChange={(event) => {
                const value = event.currentTarget.value;
                setCode(value);
                // Le seul champ du formulaire qui fasse quelque chose.
                if (isPromoCode(value)) {
                  onClose();
                  actions.cycleSkin(0);
                }
              }}
            />
            <p className={styles.fieldNote}>{t(S.saas.modal.field_promo_note, lang)}</p>
            {/*
              Le visiteur arrive de la Steveletter : le code promis est déjà
              là. Rien ne se déclenche tant qu'il n'y touche pas — c'est la
              frappe qui change d'interface, pas le remplissage.
            */}
            {promo !== '' && (
              <p className={styles.promoThanks} role="status">
                {t(S.saas.newsletter.thanks, lang)}
              </p>
            )}
          </div>
        </div>

        <div className={styles.modalFoot}>
          {/*
            Le bouton est désactivé, et la note dit pourquoi : un bouton qui
            échoue en silence serait un bug, celui-ci est une position.
          */}
          <button type="button" className={styles.ctaPrimary} disabled>
            {t(S.saas.modal.submit, lang)}
          </button>
          <p className={styles.fieldNote}>{t(S.saas.modal.submit_note, lang)}</p>
          <p className={styles.legal}>{t(S.saas.modal.legal, lang)}</p>
        </div>
      </div>
    </div>
  );
}
